/**
 * Client-side download helpers shared by the report grid and the detail view.
 *
 * Why fetch->blob instead of <a download>: the HTML download attribute is ignored for
 * cross-origin URLs, and our media lives on the blob-storage origin — a plain anchor
 * just navigates and the browser plays the mp4 inline. Fetching to a Blob and clicking
 * a temporary same-origin object-URL anchor makes the download attribute effective.
 */

/**
 * Derives a best-effort file name from a URL for client-side downloads.
 */
export const getDownloadFileNameFromUrl = (url: string, fallback = "download.bin"): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    const leaf = parsed.pathname.split("/").filter(Boolean).pop();
    if (!leaf) {
      return fallback;
    }
    const decoded = decodeURIComponent(leaf);
    if (/[<>:"/\\|?*]/.test(decoded)) {
      return fallback;
    }

    return decoded;
  } catch {
    return fallback;
  }
};

/**
 * Triggers a browser download by fetching a resource as blob and clicking a temporary anchor.
 */
export const downloadResource = async (url: string): Promise<void> => {
  const response = await fetch(url, { method: "GET" });
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = getDownloadFileNameFromUrl(url);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(downloadUrl);
};
